import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, Text } from '@nimbus-ds/components'
import { goalService, formatMetricValue, merchantTrend, merchantStatus, type Goal } from '@/api/goals'
import { funnelService } from '@/api/funnel'
import { authService } from '@/api/auth'
import { OffStorefrontCard, type OffStorefrontOrders } from '@/components/analytics/OffStorefrontCard'
import { RevenueAnalyticsCard, type RevenueAnalyticsInput } from '@/components/analytics/RevenueAnalyticsCard'

// Plain-language status line per goal status (merchant-facing, no jargon).
// `traffic` is the mix-router's acquisition disposition — distinct from a store problem.
const STATUS_LINE: Record<Goal['status'] | 'traffic', string> = {
  calibrating: 'Aprendiendo cómo funciona tu tienda. En unos días vas a ver resultados.',
  on_track: 'Vas bien.',
  watch: 'Para mirar de cerca.',
  at_risk: 'Necesita atención: revisá tu tienda (checkout, página).',
  traffic: 'Es el tráfico, no la tienda — revisá tus fuentes o hablá con quien maneja tus anuncios.',
  achieved: '¡Objetivo logrado!',
}

// Worst-first so we can surface the one thing that matters today. Store problems (at_risk) outrank a
// traffic shift; both need attention and outrank "watch".
const PRIORITY: Record<Goal['status'] | 'traffic', number> = {
  at_risk: 5, traffic: 4, watch: 3, on_track: 2, achieved: 1, calibrating: 0,
}

const TREND_STYLE = {
  up: { cls: 'text-green-600', glyph: '▲' },
  down: { cls: 'text-red-600', glyph: '▼' },
  flat: { cls: 'text-muted-foreground', glyph: '▪' },
} as const

function GoalCard({ goal }: { goal: Goal }) {
  const c = goal.primary_metric.current
  const m = merchantStatus(goal)               // mix-guard-adjusted (P0.2)
  const isCalibrating = m.status === 'calibrating'
  const trend = merchantTrend(c)
  return (
    <Card>
      <Card.Body>
        <div className="flex items-start gap-3">
          <span className="text-2xl leading-none">{m.light}</span>
          <div className="min-w-0 flex-1">
            <Text fontWeight="medium" color="neutral-textHigh" className="leading-tight">{goal.name}</Text>
            {isCalibrating ? (
              <Text className="mt-1">{STATUS_LINE[m.status]}</Text>
            ) : (
              <>
                <div className="mt-1 flex items-baseline gap-2">
                  <Text as="span" fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{formatMetricValue(goal.primary_metric.binding.metric, c?.value)}</Text>
                  {trend && (
                    <span className={`text-sm font-medium ${TREND_STYLE[trend.tone].cls}`}>
                      {TREND_STYLE[trend.tone].glyph} {trend.label}
                    </span>
                  )}
                </div>
                <Text fontSize="caption">{goal.primary_metric.label}</Text>
                <Text color="neutral-textHigh" className="mt-1">{m.note || STATUS_LINE[m.status]}</Text>
              </>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

export function MerchantHome() {
  const [goals, setGoals] = useState<Goal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [offStorefront, setOffStorefront] = useState<OffStorefrontOrders | null>(null)
  const [revenue, setRevenue] = useState<RevenueAnalyticsInput | null>(null)

  useEffect(() => {
    goalService.listGoals(7)
      .then((res) => setGoals(res.goals))
      .catch((e) => setError(e?.message || 'No pudimos cargar tus objetivos.'))
      .finally(() => setLoading(false))
  }, [])

  // Supplementary, NON-blocking: in-store/POS orders excluded from the web conversion rate, so a
  // POS-heavy merchant sees their in-store sales aren't "lost". Never gates the standup view.
  useEffect(() => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) return
    funnelService.getStoreFunnelAnalytics(storeId, 7)
      .then((res: any) => {
        setOffStorefront(res?.tnx_analytics?.off_storefront_orders || null)
        setRevenue(res?.tnx_analytics || null)
      })
      .catch(() => {})
  }, [])

  if (loading) return <Text className="text-center">Cargando…</Text>
  if (error) return <Card><Card.Body><Text className="text-center">{error}</Text></Card.Body></Card>

  if (goals.length === 0) {
    return (
      <Card><Card.Body><Text className="text-center">
        Todavía no hay objetivos definidos para tu tienda. Tu consultor los va a configurar pronto.
      </Text></Card.Body></Card>
    )
  }

  // P1.1: rank by MONEY-AT-STAKE first (a high-volume amber outranks a low-volume red),
  // then mix-guard-adjusted severity as the tiebreak. "The one thing today" = the goal
  // costing the most, not the reddest.
  // Segmented goals are a CONSULTANT instrument (CR1 v1) — never surfaced to the merchant.
  const view = goals
    .filter((g) => !g.primary_metric?.segment)
    .map((g) => ({ goal: g, m: merchantStatus(g), stake: g.impact?.revenue_at_stake || 0 }))
  const sorted = [...view]
    .sort((a, b) => (b.stake - a.stake) || (PRIORITY[b.m.status] - PRIORITY[a.m.status]))
    .map((v) => v.goal)
  const attention = view.filter((v) => v.stake > 0).sort((a, b) => b.stake - a.stake)
  const top = attention[0]
  const allCalibrating = view.every((v) => v.m.status === 'calibrating')

  // The single "what matters today" banner.
  let headline: { emoji: string; title: string; sub: string }
  if (top) {
    headline = { emoji: top.m.light, title: top.goal.name, sub: top.m.note || STATUS_LINE[top.m.status] }
  } else if (allCalibrating) {
    headline = { emoji: '⏳', title: 'Estamos aprendiendo tu tienda', sub: 'En unos días vas a empezar a ver resultados.' }
  } else {
    headline = { emoji: '🎉', title: 'Todo va bien', sub: 'Tus objetivos están en camino.' }
  }

  return (
    <div className="space-y-5">
      <Card className="border-primary/30 bg-primary/5">
        <Card.Body>
          <Text fontSize="caption" className="uppercase tracking-wide">Lo más importante hoy</Text>
          <div className="mt-1 flex items-start gap-3">
            <span className="text-2xl leading-none">{headline.emoji}</span>
            <div>
              <Text fontWeight="medium" color="neutral-textHigh" className="leading-tight">{headline.title}</Text>
              <Text>{headline.sub}</Text>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="space-y-3">
        <Text fontWeight="medium">Tus objetivos</Text>
        {sorted.map((g) => <GoalCard key={g.goal_id} goal={g} />)}
      </div>

      {/* Revenue headline (merchant calm tier) — renders only when there's revenue to show. */}
      <RevenueAnalyticsCard analytics={revenue} />

      {/* Calm transparency card (merchant tier) — only renders when there are off-storefront orders. */}
      <OffStorefrontCard off={offStorefront} />

      {/* Self-serve MVP entry (B2). Placeholder link card — B5 replaces this
          with audit-personalized proposal cards. */}
      <Link to="/merchant/banner-test" className="block no-underline">
        <Card>
          <Card.Body>
            <div className="flex items-start gap-3">
              <span className="text-2xl leading-none">🖼️</span>
              <div>
                <Text fontWeight="medium" color="neutral-textHigh" className="leading-tight">
                  Probá otro banner en tu tienda
                </Text>
                <Text fontSize="caption">
                  Subí un banner nuevo y medí cuál genera más clics. Toma 5 minutos.
                </Text>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Link>
    </div>
  )
}
